'use client'
import { useCallback, type KeyboardEvent, type RefObject } from 'react'

interface TypeToSearchOptions {
  inputRef: RefObject<HTMLInputElement | null>
  hasQuery: boolean
  clearQuery: () => void
}

const isEditable = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)

/**
 * The launcher's signature: with the panel open, a printable key lands in the
 * search field wherever focus is, and Escape clears a query before it closes
 * the menu. The close itself is the desktop click-away's Escape listener on
 * document. The app hydrates the whole document, so React's own listener sits
 * on document too and plain stopPropagation would not reach a sibling
 * listener there; stopImmediatePropagation on the native event does, and the
 * click-away registers after hydration, so it is the one that gets skipped.
 */
export const useTypeToSearch = ({ inputRef, hasQuery, clearQuery }: TypeToSearchOptions) =>
  useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        if (hasQuery) {
          event.nativeEvent.stopImmediatePropagation()
          clearQuery()
          inputRef.current?.focus({ preventScroll: true })
        }
        return
      }
      if (isEditable(event.target) || event.metaKey || event.ctrlKey || event.altKey) {
        return
      }
      if (event.key.length !== 1 || event.key === ' ') {
        return
      }
      inputRef.current?.focus({ preventScroll: true })
    },
    [clearQuery, hasQuery, inputRef]
  )
